import {
  Component,
  OnInit,
  ChangeDetectorRef,
  Input,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import {
  ContractorQuarterly,
  quarterss,
  rating,
  stars,
} from '@app/shared/constants/constants';
import { ContractorProfileService } from './contractor-profile.service';
import { GlobalErrorService } from '@app/shared/services/global-error.service';
import {
  QuarterlyFeedback,
  QuarterlyFeedbackResponse,
} from '@app/shared/interfaces/contractor-profile';
import { ToastrService } from 'ngx-toastr';
import {
  releaseInfo,
  ErrorMessages,
  NavigationRoutes,
  LoadingMessages,
  ButtonLabels,
  EmployeeProfile,
  ContractorListEnum,
  contractorQuarterlyFeedback,
  contractorQuarterlyFeedbackUtils,
  ContractorTeamInfoLabels,
  Icons,
  quarterlyFeedback,
  ContractorProfileEnum,
  RoutesEnum,
  PerformanceRating,
  PERFORMANCE_RATING_OPTIONS,
  PERFORMANCE_RATING_SHORT_LABEL,
  formatQuarterBoxLabel,
} from '@app/shared/constants/global.enum';
import { VendorService } from '@app/shared/services/vendor.service';
import { NotificationService } from '@app/shared/services/notification.service';
import {
  EmployeeDetailsResponse,
  mapEmployeeToProfileSections,
  ProfileItemConfig,
  formatDate,
  FeedbackDTO,
} from './contractor-profile.config';
import {
  submitPerformanceEdit,
  submitSkillsEdit,
} from './contractor-profile.utils';
import {
  Contractors,
  TimeSheetData,
} from '@app/shared/interfaces/vendor-management-model';
import { OffboardingRequestComponent } from '@app/pages/asset-management/cwr-offboardingCard/Offboarding-DetailsScreen/offboarding-request/offboarding-request.component';

interface BreadcrumbItem {
  label: string;
  route: string | null;
  action?: () => void;
}
type BreadcrumbConfig = Record<string, BreadcrumbItem[]>;

interface QuarterBox {
  quarter: string;
  year: number;
  label: string;
  isCurrent: boolean;
  feedbacks: FeedbackDTO[];
  loading: boolean;
  error?: string | null;
}

@Component({
  selector: 'app-contractor-profile',
  templateUrl: './contractor-profile.component.html',
  styleUrls: ['./contractor-profile.component.scss'],
})
export class ContractorProfileComponent implements OnInit, OnChanges {
  currentType: string | undefined;

  vendorListQueryParams: { [key: string]: any } = {};
  @Input() contractorId: string = '';
  @Output() closingDashboard = new EventEmitter<void>();
  @Input() navigationSourceTeam: string | null = null;
  @Input() contractorsData: Contractors | null = null;
  @Output() close = new EventEmitter<void>();

  ContractorProfileEnum = ContractorProfileEnum;
  contractorQuarterlyFeedback = contractorQuarterlyFeedback;
  contractorQuarterlyFeedbackUtils = contractorQuarterlyFeedbackUtils;

  breadcrumbConfig: BreadcrumbConfig = {};
  navigationSource: string = 'default-path';

  get currentBreadcrumbItems(): BreadcrumbItem[] {
    const key = this.navigationSource || 'default-path';
    return this.breadcrumbConfig[key] || [];
  }

  initializeBreadcrumbConfig(): void {
    this.breadcrumbConfig['team-composition'] = [
      {
        label: this.breadcrumb.dashboard,
        route: null,
        action: () => this.closingDashboard.emit(),
      },
      {
        label: this.breadcrumb.teamComposition,
        route: null,
        action: () => this.close.emit(),
      },
      {
        label: this.quarterlyFeedback.Profile,
        route: '/team-management/team-composition/contractor-profile',
      },
    ];

    this.breadcrumbConfig['timesheet'] = [
      {
        label: this.quarterlyFeedback.Breadcrumb,
        route: null,
        action: () => this.close.emit(),
      },
      {
        label: this.quarterlyFeedback.Profile,
        route: null,
      },
    ];

      this.breadcrumbConfig['notifications'] = [
     {
        label: this.breadcrumb.dashboard,
        route: '/vendor-management',
        action: () => this.closingDashboard.emit(),
      },
      {
        label: this.quarterlyFeedback.Profile,
        route: null,
      },
    ];

    this.breadcrumbConfig['default-path'] = [
      { label: 'Vendor Management', route: '/vendor-management' },
      {
        label: 'Vendor List',
        route: '/vendor-management/vendors-list',
        action: () => {
          this.router.navigate(['/vendor-management/vendors-list'], {
            queryParams: {
              vendorId: this.vendorListQueryParams['vendorId'],
              type: this.vendorListQueryParams['type'],
              month: this.vendorListQueryParams['month'],
              year: this.vendorListQueryParams['year'],
              employeeId: this.vendorListQueryParams['employeeId'],
            },
          });
        },
      },
      {
        label: 'Contractor List',
        route: '/vendor-management/rto-drill',
        action: () => {
          this.router.navigate(['/vendor-management/rto-drill'], {
            queryParams: { ...this.vendorListQueryParams },
          });
        },
      },
  
      {
        label: this.quarterlyFeedback.Profile,
        route: null,
      },
    ];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['contractorId'] && changes['contractorId'].currentValue) {
      this.loadEmployeeDetails();
      this.ensureVisibleQuartersLoaded(() => {
        this.buildQuarterBoxes();
        this.getAllQuartersForYear();
      });
    }
  }

  editModals: Array<{
    opened: boolean;
    onDismiss: () => void;
    class: string;
    sectionKey: string;
    formGroup: FormGroup;
    items: ProfileItemConfig[];
    submitHandler: () => void;
  }> = [];

  cardSections: Array<{
    headerClass: string;
    iconClass: string;
    labelKey: keyof typeof contractorQuarterlyFeedback;
    items: ProfileItemConfig[];
    editHandler: () => void;
  }> = [];

  submitEdit(form: FormGroup, items: ProfileItemConfig[]): void {
    if (form && form.valid) {
      Object.keys(form.value).forEach((key) => {
        const item = items.find((i) => i.label === key);
        if (item) {
          item.value = form.value[key];
        }
      });

      const formToSection: Array<{ form: FormGroup; key: string }> = [
        { form: this.editPerformanceForm, key: 'Performance' },
        { form: this.editSkillsForm, key: 'ISkills' },
      ];
      const matched = formToSection.find((f) => f.form === form);
      if (matched) {
        this.setModalOpen(matched.key, false);
      }
    }
  }

  private applyFormToItems(form: FormGroup, items: ProfileItemConfig[]): void {
    if (!form || !items) return;

    Object.keys(form?.value || {}).forEach((key) => {
      const item = items?.find((i) => i?.label === key);
      if (item) {
        item.value = form.value?.[key];
      }
    });
  }

  submitEditPerformance(): void {
    if (!this.editPerformanceForm || this.editPerformanceForm.invalid) {
      this.editPerformanceForm?.markAllAsTouched();
      return;
    }
    submitPerformanceEdit(
      this.editPerformanceForm,
      this.contractorId,
      this.contractorProfileService,
    ).subscribe({
      next: () => {
        this.applyFormToItems(this.editPerformanceForm, this.performanceItems);
        const latSpotAwardItem = this.performanceItems.find(
          (item) => item.label === 'Last Spot Award',
        );
        if (latSpotAwardItem && latSpotAwardItem.value) {
          const date = new Date(latSpotAwardItem.value);
          latSpotAwardItem.value = formatDate(date.toISOString());
        }
        this.toaster.success(ContractorQuarterly.employeePerformanceUpdated);
        this.setModalOpen('Performance', false);
      },
      error: (err) => {
        this.toaster.error(ContractorQuarterly.employeePerformanceUpdateError);
      },
    });
  }

  submitEditSkills(): void {
    if (!this.editSkillsForm || this.editSkillsForm.invalid) {
      this.editSkillsForm?.markAllAsTouched();
      return;
    }
    submitSkillsEdit(
      this.editSkillsForm,
      this.contractorId,
      this.contractorProfileService,
    ).subscribe({
      next: () => {
        this.applyFormToItems(this.editSkillsForm, this.skillItems);
        this.toaster.success(ContractorQuarterly.employeeSkillsUpdated);
        this.setModalOpen('ISkills', false);
      },
      error: (err) => {
        this.toaster.error(ContractorQuarterly.employeeTeamInfoUpdateError);
      },
    });
  }

  editPerformanceForm!: FormGroup;
  isEditPerformanceModalOpen: boolean = false;
  editSkillsForm!: FormGroup;
  isEditSkillsModalOpen: boolean = false;

  initEditPerformanceForm(): void {
    const group: any = {};
    this.performanceItems.forEach((item) => {
      group[item.label] = [item.value, Validators.required];
    });
    this.editPerformanceForm = this.fb.group(group);
  }
  openEditPerformanceModal(): void {
    if (!this.editPerformanceForm) {
      this.initEditPerformanceForm();
    }
    this.performanceItems.forEach((item) => {
      if (this.editPerformanceForm.contains(item.label)) {
        this.editPerformanceForm.get(item.label)?.setValue(item.value);
      }
    });
    this.setModalOpen('Performance', true);
  }

  initEditSkillsForm(): void {
    const group: any = {};
    this.skillItems.forEach((item) => {
      group[item.label] = [item.value, Validators.required];
    });
    this.editSkillsForm = this.fb.group(group);
  }
  openEditSkillsModal(): void {
    if (!this.editSkillsForm) {
      this.initEditSkillsForm();
    }
    this.skillItems.forEach((item) => {
      if (this.editSkillsForm.contains(item.label)) {
        this.editSkillsForm.get(item.label)?.setValue(item.value);
      }
    });
    this.setModalOpen('ISkills', true);
  }

  private setModalOpen(sectionKey: string, open: boolean): void {
    if (sectionKey === 'Performance') this.isEditPerformanceModalOpen = open;
    if (sectionKey === 'ISkills') this.isEditSkillsModalOpen = open;
    const modal = this.editModals.find((m) => m.sectionKey === sectionKey);
    if (modal) {
      modal.opened = open;
    }
    this.cdr.detectChanges();
  }

  updateEditModals(): void {
    this.editModals = [
      {
        opened: this.isEditPerformanceModalOpen,
        onDismiss: () => {
          this.setModalOpen('Performance', false);
        },
        class: 'edit-performance-modal',
        sectionKey: 'Performance',
        formGroup: this.editPerformanceForm,
        items: this.performanceItems,
        submitHandler: () =>
          this.isEditPerformanceModalOpen == true
            ? this.submitEditPerformance()
            : null,
      },
      {
        opened: this.isEditSkillsModalOpen,
        onDismiss: () => {
          this.setModalOpen('ISkills', false);
        },
        class: 'edit-skills-modal',
        sectionKey: 'ISkills',
        formGroup: this.editSkillsForm,
        items: this.skillItems,
        submitHandler: () =>
          this.isEditSkillsModalOpen == true ? this.submitEditSkills() : null,
      },
    ];
  }

  breadcrumb = RoutesEnum;
  ContractorName: string = '';

  bannerInitials(): string {
    return (this.ContractorName || '')
      .split(/\s+/).filter(Boolean).slice(0, 2)
      .map((w) => w[0]).join('').toUpperCase();
  }

  bannerEmpId = '';
  bannerManager = '';
  bannerL4 = '';
  bannerL3 = '';
  vendorId: number = 0;
  ContractorProfile: string = '';
  showFeedbackForm = false;
  stars = stars;
  rating = rating;
  feedbackForm!: FormGroup;
  quarterlyFeedback = contractorQuarterlyFeedback;
  ContractorQuarterly = ContractorQuarterly;
  submitted = false;
  quarters = quarterss;
  ratingTypes: string[] = ['technical', 'behavioural'];
  /** Options for the new Performance Rating dropdown (form input). */
  performanceRatingOptions = PERFORMANCE_RATING_OPTIONS;

  /** Collapsible "Rating scale" reference beneath the dropdown. Collapsed by
   *  default so the form reads clean; managers who don't remember the levels
   *  click to expand. */
  showRatingScale = false;

  /** Same options shaped for the shared <emerald-dropdown> control:
   *  {label, value} pairs. Label is the short form ("5 - Exceptional",
   *  "NA - Too New to Rate") — descriptions stay on OPTIONS for tooltip
   *  / detail views. Emerald opens its menu below the trigger so we don't
   *  get the native <select> overlay behaviour. */
  performanceRatingDropdownOptions = PERFORMANCE_RATING_OPTIONS.map(o => ({
    label: o.label,
    value: o.value,
  }));

  /** Safe display-label lookup for a raw performanceRating string from the
   *  DTO — falls back to the raw value on unknown / null. Kept as a method
   *  so the template can call it without TS complaining about indexing a
   *  Record<PerformanceRating, string> with a plain string. */
  getPerformanceRatingLabel(raw?: string | null): string {
    if (!raw) return '';
    const key = raw as PerformanceRating;
    return PERFORMANCE_RATING_SHORT_LABEL[key] ?? raw;
  }

  /** Display used in historical / detail surfaces. Returns the short form
   *  ("5 - Exceptional", "NA - Too New to Rate") — description lives on
   *  OPTIONS entries for future tooltip use. */
  getPerformanceRatingFullDisplay(raw?: string | null): string {
    if (!raw) return '';
    const opt = PERFORMANCE_RATING_OPTIONS.find(o => o.value === raw);
    return opt ? opt.label : raw;
  }

  /** emerald-dropdown writes the whole {label, value} option object to the
   *  form control (see patchHiringRequestFormValues elsewhere). Normalise
   *  before sending to BE, which expects the primitive enum string only.
   *  Values come from PERFORMANCE_RATING_OPTIONS so the cast is safe.
   *  Public so the rating-scale legend template can highlight the current
   *  selection without leaking the emerald-dropdown wrapper detail. */
  readPerformanceRatingFromForm(): PerformanceRating | null {
    const raw = this.feedbackForm.value.performanceRating;
    if (raw == null) return null;
    if (typeof raw === 'string') return raw as PerformanceRating;
    if (typeof raw === 'object' && 'value' in raw) {
      const v = (raw as { value: string }).value;
      return v ? (v as PerformanceRating) : null;
    }
    return null;
  }

  imageSrc: string = 'assets/img/no-data-found.png';
  aboutItems: ProfileItemConfig[] = [];
  teamInfoItems: ProfileItemConfig[] = [];
  performanceItems: ProfileItemConfig[] = [];
  skillItems: ProfileItemConfig[] = [];
  isReleaseModalOpen: boolean = false;
  isDateInvalid: boolean = false;
  openForm: boolean = true;
  releaseInfo = releaseInfo;
  isEditMode: boolean = false;
  editingQuarterIndex: number = -1;
  editingFeedbackId: string | number = '';
  errorMessages = ErrorMessages;
  loadingMessages = LoadingMessages;
  buttonLabels = ButtonLabels;
  icons = Icons;
  contractorListEnum = ContractorListEnum;

  loading: boolean = false;
  error: string | null = null;
  hasData: boolean = false;
  currentDate: Date = new Date();

  releaseForm!: FormGroup;
  releaseTypeOptions = [
    { label: 'Voluntary', value: 'Voluntary' },
    { label: 'Involuntary', value: 'Involuntary' },
  ];

  ratingLabelMap: { [key: string]: keyof typeof contractorQuarterlyFeedback } =
    {
      technical: 'Technical',
      behavioural: 'Behavioural',
    };

  quarterBoxes: QuarterBox[] = [];
  editingQuarterBox: QuarterBox | null = null;

  // Cached list of quarter tokens (Q1..Q4) the feedback UI is allowed to show.
  // null = not yet fetched (buildQuarterBoxes falls back to showing all 4).
  // Empty array = DB says hide all — respect it.
  private visibleQuarterSet: Set<string> | null = null;

  constructor(
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private contractorProfileService: ContractorProfileService,
    private toaster: ToastrService,
    private vendorService: VendorService,
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private dialog: MatDialog,
  ) {}

  private extractContractorId(
    data: Contractors | TimeSheetData | null,
  ): string {
    if (!data) return '';
    if (
      'contractorId' in data &&
      typeof (data as Contractors).contractorId === 'string'
    ) {
      return (data as Contractors).contractorId;
    }
    if (
      'employeeId' in data &&
      typeof (data as TimeSheetData).employeeId === 'string'
    ) {
      return (data as TimeSheetData).employeeId;
    }
    return '';
  }

  ngOnInit(): void {
    if (this.navigationSourceTeam) {
      this.navigationSource = this.navigationSourceTeam;
    } else {
      this.route.params.subscribe((params) => {
        const newContractorId = params['id'] || '';
        const hasContractorIdChanged = this.contractorId !== newContractorId;
        
        if (hasContractorIdChanged) {
          this.contractorId = newContractorId;
          if (this.contractorId) {
            this.loadEmployeeDetails();
            this.ensureVisibleQuartersLoaded(() => {
              this.buildQuarterBoxes();
              this.getAllQuartersForYear();
            });
          }
        }
      });
      this.route.queryParams.subscribe((params) => {
        if (!this.contractorId) {
          this.contractorId = this.route.snapshot.paramMap.get('id') || '';
        }
        if (params['source'] === 'team-composition') {
          this.navigationSource = 'team-composition';
        } else if (params['source'] === 'notifications') {
          this.navigationSource = 'notifications';
        } else {
             this.navigationSource = 'default-path';          
        }
        this.cdr.detectChanges();
        if (this.navigationSourceTeam) {
          this.navigationSource = this.navigationSourceTeam;
        }
      });
    }

    this.initializeBreadcrumbConfig();
    this.ContractorName = this.quarterlyFeedback.ContactorName;
    this.ContractorProfile = this.quarterlyFeedback.ContractorProfile;
    this.initializeReleaseForm();
    this.initializeFeedbackForm();
    if (!this.contractorId) {
    } else {
      this.loadEmployeeDetails();
      this.ensureVisibleQuartersLoaded(() => {
        this.buildQuarterBoxes();
        this.getAllQuartersForYear();
      });
    }
  }

  // ngOnChanges is now handled at the top of the class
  private getCurrentQuarterAndYear(): { quarter: string; year: number } {
    const now = new Date();
    const month = now.getMonth();
    const qNum = Math.floor(month / 3) + 1;
    return {
      quarter: `Q${qNum}`,
      year: now.getFullYear(),
    };
  }

  trackFeedbackBox(index: number, box: QuarterBox): string {
    return `${box.year}-${box.quarter}`;
  }
  trackFeedback(index: number, feedback: FeedbackDTO): string | number | null {
    return feedback.contractorFeedbackId || index;
  }

  initializeFeedbackForm(): void {
    this.feedbackForm = this.fb.group({
      // AB: new-form Performance Rating replaces the legacy technical +
      // behavioural stars. Bucketed enum ('A' | 'B' | 'C' | 'D' | 'TOO_NEW'
      // | 'N_A') — see PerformanceRating in shared/constants/global.enum.
      performanceRating: [null, Validators.required],
      comments: ['', [Validators.required, Validators.minLength(1)]],
    });
  }

  private generateSubmittedDate(): string {
    const now = new Date();
    const utcMillis = now.getTime() + now.getTimezoneOffset() * 60000;
    const istDate = new Date(utcMillis + 330 * 60000);

    const pad = (n: number, l = 2) => String(n).padStart(l, '0');

    const submittedDate =
      `${istDate.getFullYear()}-` +
      `${pad(istDate.getMonth() + 1)}-` +
      `${pad(istDate.getDate())}T` +
      `${pad(istDate.getHours())}:` +
      `${pad(istDate.getMinutes())}:` +
      `${pad(istDate.getSeconds())}.` +
      `${pad(istDate.getMilliseconds(), 3)}`;

    return submittedDate;
  }

  formatSubmittedDate(raw: string | null | undefined): string {
    if (!raw) return '';
    const d = new Date(raw);

    if (isNaN(d.getTime())) {
      return '';
    }
    const pad = (n: number, l = 2) => String(n).padStart(l, '0');
    const day = pad(d.getDate());
    const month = pad(d.getMonth() + 1);
    const year = d.getFullYear();

    const hours = pad(d.getHours());
    const minutes = pad(d.getMinutes());
    const seconds = pad(d.getSeconds());

    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
  }

  private buildQuarterBoxes(): void {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    const currentQuarter = Math.floor(currentMonth / 3) + 1;

    const boxes: QuarterBox[] = [];


    boxes.push({
      quarter: `Q${currentQuarter}`,
      year: currentYear,
      label: formatQuarterBoxLabel(currentQuarter, currentYear),
      isCurrent: true,
      feedbacks: [],
      loading: true,
      error: null,
    });

    let quarter = currentQuarter;
    let year = currentYear;

    for (let i = 1; i <= 3; i++) {
      quarter = quarter - 1;
      if (quarter === 0) {
        quarter = 4;
        year = year - 1;
      }

      boxes.push({
        quarter: `Q${quarter}`,
        year: year,
        label: formatQuarterBoxLabel(quarter, year),
        isCurrent: false,
        feedbacks: [],
        loading: true,
        error: null,
      });
    }

    this.quarterBoxes = this.applyVisibleQuarterFilter(boxes);
  }

  /** Filter the built 4-box window through the DB-driven visible set, then
   *  keep only the most-recent occurrence per quarter token. If the set is
   *  null (fetch hasn't landed / errored), fall back to the unfiltered list. */
  private applyVisibleQuarterFilter(boxes: QuarterBox[]): QuarterBox[] {
    if (this.visibleQuarterSet === null) return boxes;
    const seen = new Set<string>();
    return boxes.filter((b) => {
      if (!this.visibleQuarterSet!.has(b.quarter)) return false;
      if (seen.has(b.quarter)) return false;
      seen.add(b.quarter);
      return true;
    });
  }

  /** One-shot fetch of the visible quarter list; subsequent calls use cache. */
  private ensureVisibleQuartersLoaded(cb: () => void): void {
    if (this.visibleQuarterSet !== null) { cb(); return; }
    this.vendorService.getVisibleFeedbackQuarters().subscribe({
      next: (list) => { this.visibleQuarterSet = new Set(list ?? []); cb(); },
      error: () => { this.visibleQuarterSet = new Set(); cb(); },
    });
  }

  getAllQuartersForYear(): void {
    this.quarterBoxes.forEach((box) => {
      box.loading = true;
      box.error = null;

      this.contractorProfileService
        .getQuarterlyFeedback(this.contractorId, String(box.year), box.quarter)
        .subscribe({
          next: (resp: QuarterlyFeedbackResponse) => {
            const payload = resp?.data ?? resp;
            const arr: FeedbackDTO[] = Array.isArray(payload)
              ? payload
              : payload
                ? [payload]
                : [];
            if (arr.length > 0) {
              ({
                quarter: arr[0].quarter,
                year: arr[0].year,
                id: arr[0].contractorFeedbackId,
              });
            }

            arr.sort(
              (a, b) =>
                new Date(b.submittedDate || 0).getTime() -
                new Date(a.submittedDate || 0).getTime(),
            );
            box.feedbacks = arr;
            box.loading = false;
          },
          error: (err) => {
            box.feedbacks = [];
            box.error = 'Failed to load';
            box.loading = false;
            this.toaster.error(this.ContractorQuarterly.failedToLoadFeedback);
          },
        });
    });
  }

  submitFeedback(): void {
    this.submitted = true;
    if (!this.feedbackForm.valid) {
      this.feedbackForm.markAllAsTouched();
      return;
    }

    this.isEditMode ? this.updateFeedback() : this.saveFeedback();
    this.showFeedbackForm = false;
    this.isEditMode = false;
    this.editingQuarterBox = null;
  }

  setRating(type: string, star: number): void {
    this.feedbackForm.get(type)?.patchValue(star);
    this.feedbackForm.markAsDirty();
  }

  public getQuarterRating(quarter: any, type: string): number {
    if (!quarter) {
      return 0;
    }
    const propertyMap: { [key: string]: string } = {
      technical: ContractorQuarterly.technical,
      behavioural: ContractorQuarterly.behavioural,
    };
    const property = propertyMap[type] || type;
    return Number(quarter[property]) || 0;
  }

  updateFeedback(): void {
    if (!this.editingQuarterBox || !this.editingFeedbackId) return;
    const userProfile = sessionStorage.getItem('userProfile');
    if (!userProfile) {
      this.toaster.error(this.ContractorQuarterly.profileError);
      return;
    }
    const { email } = JSON.parse(userProfile);
    const submittedDate = this.generateSubmittedDate();

    const feedbackData = {
      contractorFeedbackId: Number(this.editingFeedbackId),
      quarter: this.editingQuarterBox.quarter,
      year: this.editingQuarterBox.year,
      performanceRating: this.readPerformanceRatingFromForm(),
      comments: this.feedbackForm.value.comments,
      submittedBy: email,
      submittedDate,
      employeeId: this.contractorId,
    };
    this.contractorProfileService
      .updateQuarterlyFeedback(feedbackData)
      .subscribe({
        next: () => {
          this.toaster.success(this.ContractorQuarterly.updateSuccess);
          this.getAllQuartersForYear();
          this.editingQuarterBox = null;
          this.refreshNotifications();
        },
        error: () => this.toaster.error(this.ContractorQuarterly.updateError),
      });
  }

  getQuarterlyFeedbackLabel(type: string): string {
    if (this.ratingLabelMap && this.quarterlyFeedback) {
      const labelKey = this.ratingLabelMap[type];
      return this.quarterlyFeedback[labelKey] || type;
    }
    return type;
  }

  private loadEmployeeDetails(): void {
    if (!this.contractorId) {
      this.error = ErrorMessages.NoEmployeeId;
      this.hasData = false;
      return;
    }

    this.loading = true;
    this.error = null;
    this.hasData = false;
    this.vendorService.getEmployeeDetails(this.contractorId).subscribe({
      next: (response) => {
        this.loading = false;

        if (
          response?.status === this.contractorListEnum.Success &&
          response?.data
        ) {
          this.mapEmployeeDataToItems(response.data);
          this.hasData = true;
        } else if (response?.status === this.contractorListEnum.Failed) {
          this.error =
            response?.message ||
            `${ErrorMessages.EmployeeNotFound} with ID ${this.contractorId}`;
        } else {
          this.error = ErrorMessages.InvalidResponse;
        }
      },
      error: (err) => {
        this.loading = false;
        this.error = ErrorMessages.EmployeeNotFound;
        this.hasData = false;
      },
    });
  }

  private mapEmployeeDataToItems(data: EmployeeDetailsResponse): void {
    // Restore profileSections initialization
    const profileSections = mapEmployeeToProfileSections(data);
    // Always use queryParams from route
    const previousParams: any = { ...this.route.snapshot.queryParams };

    this.ContractorName = data?.employeeName || '';
    this.ContractorProfile = data?.jobRole || '';
    this.bannerEmpId = data?.employeeId || '';
    this.bannerManager = data?.reportsTo || '';
    this.bannerL4 = data?.l4Leader || '';
    this.bannerL3 = Array.isArray(data?.l3Leader)
      ? (data?.l3Leader || []).join(', ')
      : (data?.l3Leader || '');
    this.vendorId = data?.vendorId || 0;

    // Use employeeId from queryParams, not contractorId
    const employeeId = previousParams['employeeId'];
    const validEmployeeId = employeeId && !isNaN(Number(employeeId));

    // Build query params for breadcrumb navigation
    this.vendorListQueryParams = {
      ...previousParams, // always preserve all previous params
      vendorId:
        previousParams['vendorId'] ??
        (data?.vendorId && data?.vendorId !== 0 ? data.vendorId : 1),
      month: previousParams['month'] ?? new Date().getMonth() + 1,
      year: previousParams['year'] ?? new Date().getFullYear(),
      ...(validEmployeeId ? { employeeId } : {}),
    };

    this.aboutItems = profileSections.about;
    this.teamInfoItems = profileSections.teamInfo.filter(
      (item) =>
        item.label !== ContractorTeamInfoLabels.WorksWith &&
        item.label !== ContractorTeamInfoLabels.FunctionalLead,
    );
    this.performanceItems = profileSections.performance;
    this.skillItems = profileSections.skills;

    this.initEditPerformanceForm();
    this.initEditSkillsForm();

    this.updateEditModals();
    this.cardSections = [
      {
        headerClass: 'header-row about-header-row',
        iconClass: 'info-icon',
        labelKey: 'About',
        items: this.aboutItems,
        editHandler: () => {},
      },
      {
        headerClass: 'header-row',
        iconClass: 'team-icon',
        labelKey: 'TeamInfo',
        items: this.teamInfoItems,
        editHandler: () => {},
      },
      {
        headerClass: 'header-row',
        iconClass: 'performance-icon',
        labelKey: 'Performance',
        items: this.performanceItems,
        editHandler: this.openEditPerformanceModal.bind(this),
      },
      {
        headerClass: 'header-row',
        iconClass: 'skill-icon',
        labelKey: 'ISkills',
        items: this.skillItems,
        editHandler: this.openEditSkillsModal.bind(this),
      },
    ];
  }

  saveFeedback(): void {
    this.submitted = true;
    if (this.feedbackForm.invalid || !this.editingQuarterBox) {
      this.feedbackForm.markAllAsTouched();
      return;
    }

    const userProfile = sessionStorage.getItem('userProfile');
    if (!userProfile) {
      this.toaster.error(this.ContractorQuarterly.profileError);
      return;
    }

    const { email } = JSON.parse(userProfile);
    const submittedTimestamp = this.generateSubmittedDate();
    const feedbackData = {
      quarter: this.editingQuarterBox.quarter,
      performanceRating: this.readPerformanceRatingFromForm(),
      comments: this.feedbackForm.value.comments,
      submittedBy: email,
      submittedDate: submittedTimestamp,
      employeeId: this.contractorId,
      year: this.editingQuarterBox.year,
      createdBy: email,
    };

    this.contractorProfileService.saveQuarterlyFeedback(feedbackData).subscribe(
      () => {
        this.toaster.success(this.ContractorQuarterly.saveSuccess);
        this.getAllQuartersForYear();
        this.refreshNotifications();
      },
      (error) => {
        this.toaster.error(this.ContractorQuarterly.saveError);
      },
    );
  }

  onAddFeedback(box: QuarterBox) {
    this.showFeedbackForm = true;
    this.isEditMode = false;
    this.editingFeedbackId = '';
    this.editingQuarterBox = box;
    this.feedbackForm.reset({ performanceRating: null, comments: '' });
  }

  /** Abort the current Add / Edit — closes the form without saving and
   *  drops any pending edits. */
  cancelFeedback(): void {
    this.editingQuarterBox = null;
    this.showFeedbackForm = false;
    this.isEditMode = false;
    this.editingFeedbackId = '';
    this.submitted = false;
    this.feedbackForm.reset({ performanceRating: null, comments: '' });
  }

  onEditFeedback(box: QuarterBox, feedback: FeedbackDTO): void {
    if (!feedback) return;

    this.isEditMode = true;
    this.editingFeedbackId = feedback?.contractorFeedbackId || '';
    this.editingQuarterBox = box;
    this.showFeedbackForm = true;
    if (!this.feedbackForm) this.initializeFeedbackForm();
    this.populateFeedbackForm(feedback as QuarterlyFeedback);
    this.submitted = false;
  }

  populateFeedbackForm(quarterData: QuarterlyFeedback): void {
    if (!this.feedbackForm) {
      this.initializeFeedbackForm();
    }
    // AB: if this row was captured under the new form, preload its bucket.
    // emerald-dropdown compares its selected option by reference against
    // the options array, so we must pass the whole {label, value} object,
    // not the raw string, for the pill to render the selection.
    // Legacy rows (only integer technical/behavioural set) leave the
    // dropdown blank — user must pick a bucket before Save.
    const raw = quarterData?.performanceRating ?? null;
    const preselected = raw
      ? this.performanceRatingDropdownOptions.find(o => o.value === raw) ?? null
      : null;
    this.feedbackForm.setValue({
      performanceRating: preselected,
      comments: quarterData?.comments ?? '',
    });
    this.feedbackForm.markAsPristine();
    this.feedbackForm.markAsUntouched();
  }

  private getFeedbackTimestamp(q: any): number {
    const raw = q.rawSubmittedDate || q.submittedDate;
    const t = new Date(raw).getTime();
    return isNaN(t) ? 0 : t;
  }

  canEditFeedback(box: QuarterBox, entry: FeedbackDTO): boolean {
    if (!entry || !box || box.feedbacks.length === 0) return false;
    const latest = box.feedbacks.reduce((acc, cur) => {
      const accTs = this.getFeedbackTimestamp(acc);
      const curTs = this.getFeedbackTimestamp(cur);

      if (curTs > accTs) return cur;
      if (curTs === accTs) {
        const accId = Number(acc.contractorFeedbackId) || 0;
        const curId = Number(cur.contractorFeedbackId) || 0;
        return curId > accId ? cur : acc;
      }
      return acc;
    }, box.feedbacks[0]);
    return latest === entry;
  }

  releaseDetails = [
    { key: 'releaseDate', value: this.releaseInfo.releaseDate },
    { key: 'releaseType', value: this.releaseInfo.releaseType },
    { key: 'submittedby', value: this.releaseInfo.submittedby },
  ];

  private initializeReleaseForm(): void {
    this.releaseForm = this.fb.group({
      comments: ['', Validators.required],
      releaseType: [null, Validators.required],
      releaseDate: ['', [Validators.required, this.dateValidator]],
    });
  }

  openReleaseModal(): void {
     const dialogRef: MatDialogRef<OffboardingRequestComponent> = this.dialog.open(
      OffboardingRequestComponent,
      {
        disableClose: false,
        panelClass: 'team-composition-release-modal',
        data: {
          isModal: true,
          psId: this.contractorId,
          disablePsId: true,
        },
      },
    );
  }

  closeReleaseModal(): void {
    this.isReleaseModalOpen = false;
    this.resetForm();
  }

  resetModal(name: any): void {
    if (name == 'Performance') {
      this.isEditPerformanceModalOpen = false;
    }
    this.resetForm();
  }

  dateValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;
    const dt = new Date(control.value);
    if (isNaN(dt.getTime())) return { invalidDateFormat: true };
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return dt < today ? { invalidDate: true } : null;
  }

  submitRelease(): void {
    if (!this.isDateInvalid && this.releaseForm.valid) {
      const submittedBy = JSON.parse(
        sessionStorage.getItem('userProfile') as string,
      );
      const releaseData = {
        employeeId: this.contractorId || '',
        vendorId: this.vendorId,
        releaseDate: this.releaseForm.value.releaseDate || '',
        comments: this.releaseForm.value.comments || '',
        submittedBy: `${submittedBy.firstName} ${submittedBy.lastName}` || '',
        releaseType: this.releaseForm.value.releaseType?.value || '',
      };
      if (releaseData?.employeeId && releaseData?.releaseDate) {
        this.contractorProfileService
          .releaseFormData(releaseData, this.contractorId)
          .subscribe({
            next: (response) => {
              if (response) {
                this.toaster.success(quarterlyFeedback.releaseForm);
                this.closeReleaseModal();
              }
            },
            error: (err) => {
              this.toaster.error(quarterlyFeedback.releaseErrorMsg);
            },
          });
      }
    }
  }

  resetEditState(): void {
    this.isEditMode = false;
    this.editingQuarterIndex = -1;
    this.editingFeedbackId = '';
    this.editingQuarterBox = null;
    this.showFeedbackForm = false;
    this.submitted = false;
  }

  private resetForm(): void {
    this.releaseForm.patchValue({
      comments: '',
      releaseType: null,
      releaseDate: '',
    });

    this.openForm = false;
    setTimeout(() => {
      this.openForm = true;
    }, 0);

    this.releaseForm.markAsPristine();
    this.releaseForm.markAsUntouched();
  }
  navigateBack() {
    this.close.emit();
  }

  private refreshNotifications(): void {
    this.notificationService.triggerNotificationRefresh();
  }
}
